import { apiClient } from 'api/apiClient';
import { OutOfTreatment } from 'assets/icons';
import {
  AttachmentMessageResponseType,
  FeedListInfoResponse,
  GetMessagesAttachmentsRequestParams,
  GetPatientCasesCount,
  GetPatientCasesParams,
  GetPatientMessagesRequestParams,
  MessagesType,
  NullablePartial,
  PatientCase,
  PatientFilesIdByType,
  PatientListInfoResponse,
  PatientMessagesResponse,
  TreatmentPlanTask,
  TypeOfMessages,
} from 'types';
import { transformObjIntoParams } from 'utils/transformObjIntoParams';

const SERVICE_PATH = '/ps';

const getPath = (path = '', isPublic = false) => {
  return `${apiClient.getPath(SERVICE_PATH, isPublic)}${apiClient.Paths.PATIENT_CASE}${path}`;
};

export type CreatePatinetCaseReqBody = {
  externalPatientId: number;
  firstName: string;
  lastName: string;
  oralHealthTherapist: string;
  dateOfBirth: string;
};

export type CreatePatientMessageReqBody = {
  body?: string;
  type: TypeOfMessages;
  hashtags?: string[];
  attachmentFileId?: string;
  attachmentLink?: string;
  replyToMessageId?: number;
};

export type PatchPatientCaseReqBody = Partial<
  Pick<
    PatientCase,
    | 'isTaskBlocked'
    | 'taskBlockReason'
    | 'taskBlockComment'
    | 'isArchived'
    | 'firstName'
    | 'lastName'
    | 'oralHealthTherapist'
    | 'patientComplaint'
    | 'task'
    | 'scan3dLinks'
    | 'letterLinks'
    | 'otherLinks'
    | 'dateOfBirth'
    | 'externalPatientId'
    | 'treatmentNote'
    | 'alignerTreatmentPlanUpperOrLowerType'
  >
>;

export type PatientTreatmentAndDiagnosisReqBody = NullablePartial<Pick<PatientCase, 'treatmentPlan' | 'diagnosis'>>;

export type PatientAlignerReqBody = NullablePartial<
  Pick<PatientCase, 'treatmentPlanType' | 'treatmentNote' | 'alignerTreatmentPlanUpperOrLowerType'>
>;

export type AssignCaseTimestamp = {
  checkFrom: string;
};

export type FeedMessagesAmount = {
  newPatients: number;
  letterReview: number;
  inTreatment: number;
  finished: number;
  outOfTreatment: number;
};

export type FeedMessagesParams = {
  tasks: TreatmentPlanTask[];
  page: number;
  size: number;
  isTaskBlocked?: boolean;
};

export type sendMessagesReactionType = {
  patientCaseId: number;
  messageId: number;
  reactions: {
    reactions: string[];
  };
};

const getPatientCases = async (params: GetPatientCasesParams) => {
  const res = await apiClient.apiInstance.get<PatientListInfoResponse>(getPath(transformObjIntoParams(params)));
  return res.data as PatientListInfoResponse;
};

const getPatientCase = async (patientCaseId: number) => {
  const res = await apiClient.apiInstance.get<PatientCase>(getPath('/' + patientCaseId));
  return res.data;
};

const createPatientCase = async (params: CreatePatinetCaseReqBody) => {
  const res = await apiClient.apiInstance.post<PatientCase>(getPath(), params);
  return res.data;
};

const updatePatientCase = async (patientCaseData: PatchPatientCaseReqBody, patientCaseId: number) => {
  const res = await apiClient.apiInstance.patch<PatientCase>(getPath('/' + patientCaseId), patientCaseData);
  return res.data;
};

const getPatientCaseCount = async () => {
  const res = await apiClient.apiInstance.get<GetPatientCasesCount>(getPath('/count'));
  return res.data;
};

const assignDoctorToCase = async (assignCaseData: AssignCaseTimestamp, patientCaseId: number) => {
  const res = await apiClient.apiInstance.post<PatientCase>(
    getPath('/' + patientCaseId + '/assign' + transformObjIntoParams(assignCaseData)),
  );
  return res.data;
};

const removeAssignment = async (assignCaseData: AssignCaseTimestamp, patientCaseId: number) => {
  const res = await apiClient.apiInstance.post<PatientCase>(
    getPath('/' + patientCaseId + '/un-assign' + transformObjIntoParams(assignCaseData)),
  );
  return res.data;
};

const savePatientCaseAttachedFiles = async (patientCaseId: number, allPatientFilesIdByType: PatientFilesIdByType) => {
  const res = await apiClient.apiInstance.put<File[]>(getPath(`/${patientCaseId}/files`), allPatientFilesIdByType);
  return res.data;
};

const updatePatientDiagnosisAndTreatment = async (
  patientCaseData: PatientTreatmentAndDiagnosisReqBody,
  patientCaseId: number,
) => {
  const res = await apiClient.apiInstance.put<PatientCase>(
    getPath('/' + patientCaseId + '/diagnosis'),
    patientCaseData,
  );
  return res.data;
};

const updatePatientAlignerTreatment = async (patientCaseData: PatientAlignerReqBody, patientCaseId: number) => {
  const res = await apiClient.apiInstance.put<PatientCase>(
    getPath('/' + patientCaseId + '/aligner-final-treatment-plan'),
    patientCaseData,
  );
  return res.data;
};

const getFeedMessagesAmount = async () => {
  const res = await apiClient.apiInstance.get<FeedMessagesAmount>(getPath('/messages/count'));
  return res.data;
};

const getFeedMessages = async (params: FeedMessagesParams) => {
  const res = await apiClient.apiInstance.get<FeedListInfoResponse>(
    getPath('/messages' + transformObjIntoParams(params)),
  );
  return res.data;
};

const getPatientMessages = async (patientCaseId: number, params: GetPatientMessagesRequestParams) => {
  const res = await apiClient.apiInstance.get<PatientMessagesResponse>(
    getPath(`/${patientCaseId}/messages${transformObjIntoParams(params)}`),
  );
  return res.data as PatientMessagesResponse;
};

const readPatientMessages = async (patientCaseId: number) => {
  const res = await apiClient.apiInstance.put(getPath(`/${patientCaseId}/messages/read`));
  return res.data;
};

const sendPatientMessage = async (patientCaseId: number, params: CreatePatientMessageReqBody) => {
  const res = await apiClient.apiInstance.post<MessagesType>(getPath(`/${patientCaseId}/messages`), params);
  return res.data;
};

const getMessagesAttachments = async (patientCaseId: number, params: GetMessagesAttachmentsRequestParams) => {
  const res = await apiClient.apiInstance.get<AttachmentMessageResponseType>(
    getPath(`/${patientCaseId}/messages/attachments${transformObjIntoParams(params)}`),
  );
  return res.data as AttachmentMessageResponseType;
};

const getActivityFeedCases = async (params: GetPatientCasesParams) => {
  const res = await apiClient.apiInstance.get<PatientListInfoResponse>(
    getPath(`/activity${transformObjIntoParams(params)}`),
  );
  return res.data as PatientListInfoResponse;
};

const dismissPatientCase = async (patientCaseId: number) => {
  await apiClient.apiInstance.post(getPath(`/${patientCaseId}/dismiss`));
};

const sendPatientMessageReaction = async ({ patientCaseId, messageId, reactions }: sendMessagesReactionType) => {
  const res = await apiClient.apiInstance.put<MessagesType>(
    getPath(`/${patientCaseId}/messages/${messageId}/reaction`),
    reactions,
  );
  return res.data;
};

const deletePatientCase = async (patientCaseId: number) =>
  await apiClient.apiInstance.delete(getPath('/' + patientCaseId));

export const patientService = {
  getPatientCases,
  createPatientCase,
  getPatientCase,
  updatePatientCase,
  getPatientCaseCount,
  assignDoctorToCase,
  removeAssignment,
  savePatientCaseAttachedFiles,
  updatePatientDiagnosisAndTreatment,
  getFeedMessages,
  getFeedMessagesAmount,
  getPatientMessages,
  sendPatientMessage,
  readPatientMessages,
  getMessagesAttachments,
  getActivityFeedCases,
  dismissPatientCase,
  sendPatientMessageReaction,
  deletePatientCase,
  updatePatientAlignerTreatment,
};
